import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type HTMLAttributes,
  type ReactNode
} from "react";
import { cn } from "@/lib/cn.js";

/**
 * Lightweight dialog primitive. We don't pull in @radix-ui/react-dialog because
 * that would expand the dep footprint significantly for what is mostly modal-CSS
 * + escape-key-handling. The behavior here matches Radix's headless contract for
 * Dialog (open / onOpenChange / portal / overlay) at the level shadcn/ui uses.
 */

interface DialogContextValue {
  open: boolean;
  setOpen: (next: boolean) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function Dialog({ open, defaultOpen, onOpenChange, children }: DialogProps): React.JSX.Element {
  const [internal, setInternal] = useState(defaultOpen ?? false);
  const isControlled = open !== undefined;
  const value = isControlled ? open : internal;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternal(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const ctx = useMemo(() => ({ open: value, setOpen }), [value, setOpen]);
  return <DialogContext.Provider value={ctx}>{children}</DialogContext.Provider>;
}

function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("Dialog primitives must be used inside <Dialog>");
  return ctx;
}

export function DialogTrigger({ children, asChild: _asChild, ...props }: HTMLAttributes<HTMLButtonElement> & { asChild?: boolean }): React.JSX.Element {
  const { setOpen } = useDialog();
  return (
    <button
      type="button"
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(true);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export function DialogContent({ className, children, ...props }: HTMLAttributes<HTMLDivElement>): React.JSX.Element | null {
  const { open, setOpen } = useDialog();
  useEffect(() => {
    if (!open) return;
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, setOpen]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm animate-fade-in"
        onClick={() => setOpen(false)}
        aria-hidden
      />
      <div
        className={cn(
          "relative z-10 grid w-full max-w-lg gap-4 rounded-xl border bg-card p-6 shadow-lg animate-scale-in sm:max-w-lg",
          // Cap height to viewport with breathing room and let inner content
          // scroll. Without this, long forms (services, schedules, closures,
          // recurrence editor) push the footer below the fold on phones.
          "max-h-[calc(100dvh-2rem)] overflow-y-auto",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div className={cn("flex flex-col space-y-1.5 text-left", className)} {...props} />;
}

export function DialogTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>): React.JSX.Element {
  return <h2 className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />;
}

export function DialogDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>): React.JSX.Element {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />;
}

export function DialogClose({ children, ...props }: HTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  const { setOpen } = useDialog();
  return (
    <button
      type="button"
      {...props}
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(false);
      }}
    >
      {children}
    </button>
  );
}
